import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from "recharts";
import { Droplets, Fuel, Wallet, CreditCard, Banknote, Calendar, ArrowUpRight, TrendingUp, Users } from "lucide-react";
import { toast } from "react-toastify";
import Loader from "../Loader";
import { useLanguage } from "../../context/LanguageContext";
import { API } from "../../utils";

const Dashboard = () => {
  const { t } = useLanguage();
  const [summary, setSummary] = useState({
    petrolSales: 0,
    dieselSales: 0,
    oilSales: 0,
    cashTotal: 0,
    onlineTotal: 0,
    creditTotal: 0,
    grandTotal: 0,
    petrolLiters: 0,
    dieselLiters: 0,
    oilPcs: 0,
    entriesToday: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const response = await API.get(`/api/dashboard?filter=${filter}&date=${selectedDate}`);
        const result = response.data;
        if (result.success && result.data) {
          setSummary({
            petrolSales: result.data.petrolSales || 0,
            dieselSales: result.data.dieselSales || 0,
            oilSales: result.data.oilSales || 0,
            cashTotal: result.data.cashTotal || 0,
            onlineTotal: result.data.onlineTotal || 0,
            creditTotal: result.data.creditTotal || 0,
            grandTotal: result.data.grandTotal || 0,
            petrolLiters: result.data.petrolLiters || 0,
            dieselLiters: result.data.dieselLiters || 0,
            oilPcs: result.data.oilPcs || 0,
            entriesToday: result.data.entriesToday || 0
          });
        }
      } catch (error) {
        console.error("Dashboard fetch error:", error);
        const errorMsg = error.response?.data?.error || t("dashboard.fetchError");
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [filter, selectedDate]);

  const pieData = [
    { name: t("common.petrol"), value: summary.petrolSales, color: "#059669" },
    { name: t("common.diesel"), value: summary.dieselSales, color: "#4f46e5" },
    { name: t("common.oil"), value: summary.oilSales, color: "#d97706" },
  ].filter(item => item.value > 0);

  const totalCollections = (summary.cashTotal || 0) + (summary.onlineTotal || 0) + (summary.creditTotal || 0);

  const Card = ({ title, value, subtitle, icon: Icon, colorClass, gradient }) => (
    <div className={`relative overflow-hidden bg-white p-5 rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md group flex flex-col justify-between`}>
      <div className={`absolute top-0 right-0 w-32 h-32 -mr-8 -mt-8 rounded-full opacity-[0.03] dark:opacity-[0.05] bg-current transition-transform group-hover:scale-110 ${colorClass}`}></div>
      <div className="flex justify-between items-start relative z-10">
        <div className="space-y-1 w-full pr-2">
          <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{title}</p>
          <h3 className="text-lg xs:text-xl sm:text-2xl lg:text-lg xl:text-2xl font-extrabold text-gray-800 dark:text-white tracking-tight whitespace-nowrap">{value}</h3>
          <p className="text-[12px] font-bold text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
        <div className={`p-3 rounded-xl shadow-lg ${gradient} transform transition-transform group-hover:scale-110 flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-10 transition-colors duration-300">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-800 dark:text-white tracking-tight">{t("dashboard.title")}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Monitoring your business performance</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex flex-wrap bg-gray-100 dark:bg-gray-800 p-1 rounded-xl shadow-inner w-full sm:w-auto overflow-hidden">
            {["all", "day", "week", "month"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`flex-1 min-w-[60px] px-2 sm:px-4 py-2 rounded-lg text-[10px] sm:text-[11px] font-bold transition-all uppercase tracking-wider ${
                  filter === f ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-white shadow-sm" : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                }`}>{t(`common.${f}`)}</button>
            ))}
          </div>
          
          <div className="flex items-center space-x-2 bg-white dark:bg-[#1e293b] border border-gray-100 dark:border-gray-800 px-4 py-2 rounded-xl shadow-sm w-full sm:w-auto">
            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input 
              type="date" 
              className="outline-none bg-transparent text-sm font-bold text-gray-700 dark:text-gray-300 w-full" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)} 
            />
          </div>
        </div>
      </div>

      {loading && <Loader message="KAVIRAJ AGENCIES" />}
      {!loading && (
        <>
          {/* Top Row: Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {/* GRAND TOTAL */}
            <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-blue-700 p-5 sm:p-6 rounded-2xl shadow-xl shadow-indigo-500/20 group flex flex-col justify-between">
              <div className="absolute top-0 right-0 w-32 h-32 -mr-8 -mt-8 bg-white opacity-10 rounded-full group-hover:scale-125 transition-transform"></div>
              <div className="relative z-10 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold text-indigo-100 uppercase tracking-widest">{t("dashboard.totalSales")}</p>
                  <TrendingUp className="w-5 h-5 text-indigo-200" />
                </div>
                <h3 className="text-xl xs:text-2xl sm:text-3xl lg:text-xl xl:text-3xl font-extrabold text-white tracking-tighter whitespace-nowrap">
                  ₹ {(summary.grandTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </h3>
                <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full w-fit">
                  <ArrowUpRight className="w-3 h-3 text-white" />
                  <span className="text-[10px] font-bold text-white uppercase tracking-wider">Active Operations</span>
                </div>
              </div>
            </div>

            <Card 
              title={t("dashboard.petrolSales")}
              value={`₹ ${(summary.petrolSales || 0).toLocaleString('en-IN')}`}
              subtitle={`${(summary.petrolLiters || 0).toFixed(2)} ${t("common.liters")}`}
              icon={Fuel}
              colorClass="text-emerald-500"
              gradient="bg-gradient-to-br from-emerald-400 to-emerald-600"
            />

            <Card 
              title={t("dashboard.dieselSales")}
              value={`₹ ${(summary.dieselSales || 0).toLocaleString('en-IN')}`}
              subtitle={`${(summary.dieselLiters || 0).toFixed(2)} ${t("common.liters")}`}
              icon={Fuel}
              colorClass="text-blue-500"
              gradient="bg-gradient-to-br from-blue-400 to-blue-600"
            />

            <Card 
              title={t("dashboard.oilSales")}
              value={`₹ ${(summary.oilSales || 0).toLocaleString('en-IN')}`}
              subtitle={`${summary.oilPcs || 0} ${t("common.pcs")}`}
              icon={Droplets}
              colorClass="text-amber-500"
              gradient="bg-gradient-to-br from-amber-400 to-amber-600"
            />
          </div>

          {/* Middle Row: Collections & Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Collection Details */}
            <div className="lg:col-span-1 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-50 dark:border-gray-800">
                <h3 className="text-sm font-extrabold text-gray-800 dark:text-white uppercase tracking-wider">{t("dashboard.paymentSummary")}</h3>
              </div>
              
              <div className="p-6 space-y-6 flex-grow">
                {[
                  { name: t("common.cash"), amount: summary.cashTotal, icon: Banknote, color: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" },
                  { name: t("common.online"), amount: summary.onlineTotal, icon: Wallet, color: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" },
                  { name: t("common.credit"), amount: summary.creditTotal, icon: CreditCard, color: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400" }
                ].map((item) => (
                  <div key={item.name} className="flex justify-between items-center group cursor-default">
                    <div className="flex items-center space-x-4">
                      <div className={`p-2.5 rounded-xl transition-transform group-hover:scale-110 ${item.color}`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{item.name}</span>
                    </div>
                    <span className="text-sm font-extrabold text-gray-800 dark:text-white whitespace-nowrap ml-2">₹ {(item.amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>

              <div className="mt-auto p-6 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{t("dashboard.totalCollections")}</span>
                  <span className="text-lg font-black text-indigo-600 dark:text-indigo-400 whitespace-nowrap ml-2">₹ {totalCollections.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            {/* Visual Breakdown */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col">
              <h3 className="text-sm font-extrabold text-gray-800 dark:text-white uppercase tracking-wider mb-8">{t("dashboard.salesBreakdown")}</h3>
              
              <div className="flex flex-col md:flex-row items-center justify-around flex-grow space-y-8 md:space-y-0">
                {summary.grandTotal > 0 ? (
                  <>
                    <div className="w-64 h-64 relative flex justify-center items-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            innerRadius={70}
                            outerRadius={95}
                            paddingAngle={5}
                            dataKey="value"
                            stroke="none"
                            cornerRadius={10}
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                            formatter={(value) => `₹ ${(value || 0).toLocaleString('en-IN')}`} 
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col justify-center items-center pointer-events-none">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t("common.total")}</span>
                        <span className="text-xl font-black text-gray-800 dark:text-white">₹ {(summary.grandTotal || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </div>

                    <div className="space-y-4 min-w-[200px]">
                      {pieData.map((item) => {
                        const percentage = ((item.value / summary.grandTotal) * 100).toFixed(1);
                        return (
                          <div key={item.name} className="flex items-center justify-between group">
                            <div className="flex items-center space-x-3">
                              <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: item.color }}></div>
                              <span className="text-[13px] font-bold text-gray-700 dark:text-gray-300">{item.name}</span>
                            </div>
                            <div className="text-right">
                              <p className="text-[13px] font-black text-gray-800 dark:text-white">₹ {item.value.toLocaleString('en-IN')}</p>
                              <p className="text-[10px] font-bold text-gray-400">{percentage}%</p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-4 py-10 opacity-40">
                    <TrendingUp className="w-16 h-16 text-gray-300" />
                    <p className="text-sm font-bold text-gray-400">{t("dashboard.noSalesData")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sales Comparison Chart Section */}
          {/* <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-extrabold text-gray-800 dark:text-white uppercase tracking-wider">
                Sales Comparison (Rupees)
              </h3>
              <span className="text-xs text-gray-400 font-medium">Interactive comparison chart</span>
            </div>
            
            <div className="h-72 w-full">
              {summary.grandTotal > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: t("common.petrol"), sales: summary.petrolSales, color: "#059669" },
                    { name: t("common.diesel"), sales: summary.dieselSales, color: "#4f46e5" },
                    { name: t("common.oil"), sales: summary.oilSales, color: "#d97706" }
                  ]} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 11, fontWeight: 600 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 11, fontWeight: 600 }} tickFormatter={(val) => `₹ ${val.toLocaleString('en-IN')}`} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', backgroundColor: '#fff', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                      formatter={(value) => [`₹ ${value.toLocaleString('en-IN')}`, 'Sales']}
                    />
                    <Bar dataKey="sales" radius={[8, 8, 0, 0]} maxBarSize={50}>
                      {[
                        { color: "#059669" },
                        { color: "#4f46e5" },
                        { color: "#d97706" }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full opacity-40">
                  <TrendingUp className="w-12 h-12 text-gray-300 mb-2" />
                  <p className="text-xs font-bold text-gray-400">{t("dashboard.noSalesData")}</p>
                </div>
              )}
            </div>
          </div> */}

          {/* Bottom Row: Additional Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
             <div className="bg-white dark:bg-[#1e293b] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center space-x-4 transition-all hover:shadow-md group">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-xl transition-transform group-hover:scale-110">
                  <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{t("dashboard.entries")}</p>
                  <p className="text-base xs:text-lg sm:text-xl lg:text-lg xl:text-xl font-black text-gray-800 dark:text-white whitespace-nowrap">{summary.entriesToday || 0}</p>
                </div>
             </div>

             <div className="bg-white dark:bg-[#1e293b] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center space-x-4 transition-all hover:shadow-md group">
                <div className="bg-rose-50 dark:bg-rose-900/20 p-3 rounded-xl transition-transform group-hover:scale-110">
                  <CreditCard className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">{t("dashboard.pendingCredit")}</p>
                  <p className="text-base xs:text-lg sm:text-xl lg:text-lg xl:text-xl font-black text-rose-600 dark:text-rose-400 whitespace-nowrap">₹ {(summary.creditTotal || 0).toLocaleString('en-IN')}</p>
                </div>
             </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
